import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import CustomTable from '@src/components/CustomTable';
import CustomSelect from '@src/components/CustomSelect';
import Popup from '@src/components/Popup';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import { TOOL_TYPES, INIT_PAGINATION } from '@src/constants';
import { StyledToolManagement } from './index.style';
import CreateToolModal from './CreateToolModal';
import DetailToolModal from './DetailToolModal';

const ToolManagement = ({ appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(INIT_PAGINATION);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [selectedTool, setSelectedTool] = useState(null);
  const [deleteTool, setDeleteTool] = useState(null);

  const fetchTools = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...(appId && { appId }),
        ...(searchTerm && { searchTerm }),
        ...(typeFilter && { type: typeFilter }),
      };

      const response = await apis.toolManagement.getTools(params);
      if (response?.items) {
        setTools(response.items || []);
        setPagination({
          ...pagination,
          page: response.pagination?.currentPage || page,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.pagesCount || 0,
        });
      }
    } catch (error) {
      enqueueSnackbar(t('fetchToolsError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchTools(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    fetchTools(newPage);
  };

  const handleToggleStatus = async (tool) => {
    try {
      const response = await apis.toolManagement.updateToolStatus(
        tool.id,
        !tool.isActive,
      );
      if (response?.status) {
        enqueueSnackbar(t('updateToolStatusSuccess'), { variant: 'success' });
        fetchTools(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('updateToolStatusError'), { variant: 'error' });
    }
  };

  const handleEdit = (tool) => {
    const originalTool = tools.find((item) => item.id === tool.id);

    setSelectedTool(originalTool || tool);
    setOpenDetailModal(true);
  };

  const handleDelete = (tool) => {
    const originalTool = tools.find((item) => item.id === tool.id);
    setDeleteTool(originalTool || tool);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await apis.toolManagement.deleteTool(deleteTool.id);
      if (response?.status) {
        enqueueSnackbar(t('deleteToolSuccess'), { variant: 'success' });
        setOpenDeleteModal(false);
        setDeleteTool(null);
        fetchTools(pagination.page);
      }
    } catch (error) {
      enqueueSnackbar(t('deleteToolError'), { variant: 'error' });
    }
  };

  const handleModalClose = () => {
    setOpenCreateModal(false);
    setOpenDetailModal(false);
    setSelectedTool(null);
  };

  const handleSuccess = () => {
    fetchTools(pagination.page);
    handleModalClose();
  };

  const getTypeChip = (type) => {
    const typeConfig = {
      [TOOL_TYPES.AI_TOOL]: { label: 'AI Tool', color: 'primary' },
      [TOOL_TYPES.AI_CHAT]: { label: 'AI Chat', color: 'success' },
      [TOOL_TYPES.AI_MODEL]: { label: 'AI Model', color: 'secondary' },
    };

    const config = typeConfig[type] || { label: type, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const heads = [
    {
      label: t('toolId'),
      valueName: 'id',
      align: 'left',
    },
    {
      label: t('toolCode'),
      valueName: 'code',
      align: 'left',
    },
    {
      label: t('toolName'),
      valueName: 'name',
      align: 'left',
    },
    {
      label: t('icon'),
      valueName: 'icon',
      align: 'center',
    },
    {
      label: t('type'),
      valueName: 'type',
      align: 'center',
    },
    {
      label: t('status'),
      valueName: 'status',
      align: 'center',
    },
    {
      label: t('actions'),
      valueName: 'action',
      align: 'center',
    },
  ];

  const formatData = tools.map((tool) => ({
    ...tool,
    id: tool.id?.substring(0, 8) || '--',
    code: <Typography className="tool-code">{tool.code || '--'}</Typography>,
    name:
      tool.translations?.vi?.name ||
      tool.translations?.en?.name ||
      tool.code ||
      '--',
    icon: tool.icon ? (
      <Box display="flex" justifyContent="center">
        <img
          src={tool.icon}
          alt="icon"
          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
        />
      </Box>
    ) : (
      <Box display="flex" justifyContent="center" color="text.secondary">
        --
      </Box>
    ),
    type: getTypeChip(tool.type),
    status: (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(tool);
        }}
      >
        <FontAwesomeIcon
          icon={tool.isActive ? faToggleOn : faToggleOff}
          color={tool.isActive ? COLOR.success.base : COLOR.blueGray[64]}
          size="lg"
        />
      </IconButton>
    ),
    action: (
      <Box display="flex" gap={1} justifyContent="center">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(tool);
          }}
        >
          <Edit color="primary" fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(tool);
          }}
        >
          <Delete color="error" fontSize="small" />
        </IconButton>
      </Box>
    ),
  }));

  const typeOptions = [
    { value: '', label: t('allTypes') },
    { value: TOOL_TYPES.AI_TOOL, label: 'AI Tool' },
    { value: TOOL_TYPES.AI_CHAT, label: 'AI Chat' },
    { value: TOOL_TYPES.AI_MODEL, label: 'AI Model' },
  ];

  return (
    <StyledToolManagement>
      <Box className="header-card">
        <Box className="header-card-title">
          <Typography variant="h3">{t('toolManagement')}</Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            startIcon={<Add />}
          >
            {t('createTool')}
          </Button>
        </Box>

        <Box className="filters" display="flex">
          <Box display="flex" flex={1} gap={4}>
            <TextField
              className="search-input"
              size="small"
              placeholder={t('searchByToolName')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <CustomSelect
              fullWidth={false}
              className="type-filter"
              options={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder={t('filterByType')}
            />
          </Box>
          <Button variant="outlined" onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </Box>

      <Box className="body-card">
        <CustomTable
          actions={[]}
          items={formatData}
          heads={heads}
          loading={loading}
          pagination={pagination}
          onChangePagination={handlePageChange}
          disableRowClick
        />
      </Box>

      <CreateToolModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <DetailToolModal
        open={openDetailModal}
        tool={selectedTool}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        appId={appId}
      />

      <Popup
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title={t('confirmDeleteTool')}
        content={t('deleteToolConfirm', { name: deleteTool?.code })}
        okMessage={t('yes')}
        cancelMessage={t('no')}
        onOk={confirmDelete}
        titleColor={COLOR.warning.base}
      />
    </StyledToolManagement>
  );
};

export default ToolManagement;
